import type { Runtime } from './runtime';
import type { WorldInit } from './initializeWorld';
import type { Account } from '../account/account';
import { worldToLayout } from '../core/coords';
import { discoveryPlaces, markPoint } from '../discovery/places';
import { KeptBook } from '../discovery/keptBook';
import { bookOf } from '../discovery/book';
import { reaches } from '../discovery/reach';
import { createDiscoveryBook } from '../ui/discoveryBook';
import { createDiscoveryCard } from '../ui/discoveryCard';
import type { PaintingMinimapMark } from '../ui/types';

/** A visitor at walking pace covers a third of a metre between two looks. */
const LOOK_MS = 250;

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** Finding the city's places: a signed-in visitor who walks up to one, or rows to it, has it in
 *  their book. Walking needs no account; keeping what is found does. Flying over the city and
 *  being led on a tour find nothing; the book is the visitor's to fill. */
export function createDiscoveryRuntime(
  init: WorldInit,
  runtime: () => Runtime,
  account: Account,
  openAccount: () => void
) {
  const places = discoveryPlaces(init.data.layout);
  const marks = (): PaintingMinimapMark[] =>
    places
      .filter((place) => kept.has(place.id))
      .map((place) => ({ name: place.name, point: markPoint(place.footprint) }));
  const kept = new KeptBook(
    places.map((place) => place.id),
    storage(),
    () => {
      void account.refresh();
    },
    () => {
      runtime().minimap.setMarks(marks());
    }
  );
  const card = createDiscoveryCard((place) => {
    book.show(place);
  });
  const follow = (): void => {
    if (account.state.kind === 'signedIn') void kept.load();
    else if (kept.open) {
      kept.close();
      card.clear();
    }
  };
  const stopFollowing = account.onChange(follow);
  follow();
  const inCity = (): boolean => {
    const rt = runtime();
    return !rt.menuOpen && !rt.options.still;
  };
  const book = createDiscoveryBook(
    () => bookOf(places, kept),
    () => ({ signedIn: kept.open, failing: kept.failing }),
    () => {
      const rt = runtime();
      return (
        inCity() &&
        !rt.ui.helpOpen &&
        rt.ui.overlay !== 'visible' &&
        !rt.communications.isOpen &&
        !rt.emotes.isOpen
      );
    },
    (open) => {
      const rt = runtime();
      rt.input?.setEnabled(!open && !rt.menuOpen && !rt.ui.helpOpen && !rt.communications.isOpen);
    },
    openAccount
  );
  let looked = -Infinity;
  const look = (): void => {
    const rt = runtime();
    const { mode, pose } = rt.control.controlState;
    if (!kept.open || (mode !== 'walk' && mode !== 'boat') || rt.tours.state()) return;
    const at = worldToLayout(pose.position);
    for (const place of places)
      if (
        !kept.has(place.id) &&
        reaches(place.footprint, at, mode, rt.env.env.collision) &&
        kept.add(place.id)
      ) {
        card.show(place);
        rt.minimap.setMarks(marks());
      }
  };
  return {
    get isOpen(): boolean {
      return book.isOpen;
    },
    marks,
    diagnostics: () => ({
      total: places.length,
      found: places.filter((place) => kept.has(place.id)).map((place) => place.id),
      card: card.current,
      open: book.isOpen,
      keeping: kept.open,
      failing: kept.failing,
    }),
    update(): void {
      const now = performance.now();
      kept.update(now);
      if (inCity() && now - looked >= LOOK_MS) {
        looked = now;
        look();
      }
      card.update(now, inCity() && !book.isOpen);
      book.update();
    },
    dispose(): void {
      stopFollowing();
      book.dispose();
      card.dispose();
    },
  };
}
